import { Eye, RefreshCw } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router";

import { QueryErrorState } from "@/api/QueryErrorState";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import {
  usePreviewHistoryQuery,
  type PreviewHistoryItem,
  type PreviewHistoryInspectionState,
  type PreviewHistoryTraceStatus,
} from "@/api/previewHistory";
import { isTerminalQueryError } from "@/api/queryClient";
import { useCapability } from "@/auth/SessionContext";
import { formatDateTime } from "@/lib/format";
import {
  CAPTURE_STATE_LABELS,
  candidateDisplayVersion,
  previewRunPath,
} from "@/lib/previewInsights";
import { Badge } from "@/ui/Badge";
import { labPath } from "@/lib/labUrl";
import { Button } from "@/ui/Button";
import { Card, CardBody } from "@/ui/Card";
import { SimulatedPatientPanel } from "@/ui/preview/SimulatedPatientPanel";
import {
  DeniedState,
  EmptyState,
  StaleDataNotice,
} from "@/ui/states";

import { useProjectId } from "./ProjectLayoutRoute";

const TRACE_STATUS_LABELS = {
  waiting: "Waiting",
  live: "Live",
  completed: "Completed",
  incomplete: "Incomplete",
  unavailable: "Unavailable",
} as const satisfies Record<PreviewHistoryTraceStatus, string>;

const TRACE_STATUS_TONES = {
  waiting: "outline",
  live: "success",
  completed: "neutral",
  incomplete: "warning",
  unavailable: "destructive",
} as const satisfies Record<
  PreviewHistoryTraceStatus,
  "outline" | "success" | "neutral" | "warning" | "destructive"
>;

const INSPECTION_STATE_LABELS = {
  inspectable: "Run confirmed",
  not_inspectable: "Dispatch attempt",
  temporarily_unavailable: "Status unavailable",
} as const satisfies Record<PreviewHistoryInspectionState, string>;

const CAPTURE_STATE_TONES = {
  absent: "outline",
  pending: "outline",
  captured: "success",
  incomplete: "warning",
  failed: "destructive",
  unsupported: "warning",
} as const satisfies Record<
  NonNullable<PreviewHistoryItem["captureState"]>,
  "outline" | "success" | "warning" | "destructive"
>;

function PreviewHistoryRow({
  item,
  position,
  projectId,
}: {
  item: PreviewHistoryItem;
  position: number;
  projectId: string;
}) {
  const candidate = candidateDisplayVersion(item.candidateRevision);
  const createdAt = formatDateTime(item.createdAt);
  const inspectable = item.inspectionState === "inspectable";
  const temporarilyUnavailable =
    item.inspectionState === "temporarily_unavailable";
  const statusLabel =
    item.traceStatus !== undefined && item.traceStatus !== null
      ? TRACE_STATUS_LABELS[item.traceStatus]
      : INSPECTION_STATE_LABELS[item.inspectionState];
  const tone =
    item.traceStatus !== undefined && item.traceStatus !== null
      ? TRACE_STATUS_TONES[item.traceStatus]
      : temporarilyUnavailable
        ? "warning"
        : inspectable
          ? "outline"
          : "neutral";

  return (
    <li>
      <Card>
        <CardBody className="grid min-w-0 gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto] md:items-center">
          <div className="min-w-0 space-y-1">
            <p className="font-medium text-foreground">
              {candidate ?? "Test version unavailable"}
            </p>
            <p className="text-sm text-muted-foreground">
              <time dateTime={item.createdAt} dir="ltr">
                {createdAt}
              </time>
            </p>
          </div>

          <dl className="grid min-w-0 gap-2 text-sm sm:grid-cols-2 md:grid-cols-1">
            <div className="min-w-0">
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Preview target
              </dt>
              <dd dir="auto" className="truncate text-foreground">
                {item.previewTarget}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Target revision
              </dt>
              <dd dir="ltr" className="text-foreground">
                {item.previewTargetRevision}
              </dd>
            </div>
          </dl>

          <div className="flex min-w-0 flex-col items-start gap-2 md:items-end">
            <div className="flex flex-wrap gap-2 md:justify-end">
              <Badge tone={tone}>{statusLabel}</Badge>
              {inspectable && item.captureState ? (
                <Badge tone={CAPTURE_STATE_TONES[item.captureState]}>
                  Trace: {CAPTURE_STATE_LABELS[item.captureState]}
                </Badge>
              ) : null}
            </div>
            {inspectable ? (
              <div className="flex flex-wrap items-center gap-2">
              <Button asChild size="sm">
                <Link
                  aria-label={`View Insights for ${candidate ?? "Test version unavailable"}, history item ${position}, dispatched ${createdAt}`}
                  to={previewRunPath(projectId, item.previewRunRef)}
                >
                  <Eye aria-hidden="true" className="h-4 w-4" />
                  View Insights
                </Link>
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link
                  to={labPath(projectId, { run: item.previewRunRef })}
                  aria-label={`Open in Reliv Labs: history item ${position}`}
                >
                  Open in Reliv Labs
                </Link>
              </Button>
              </div>
            ) : (
              <p className="max-w-sm text-sm text-muted-foreground md:text-end">
                {temporarilyUnavailable
                  ? item.unavailableCode === "preview_insights_timeout"
                    ? "The recorded target timed out. Retry the history to confirm this run."
                    : "The recorded target cannot answer now. No run state is inferred."
                  : "The Editor recorded a dispatch attempt, but the recorded target did not confirm an inspectable Preview run."}
              </p>
            )}
          </div>
        </CardBody>
      </Card>
    </li>
  );
}

/**
 * Project → Previews: server-rediscovered history, never launch-dialog state.
 */
export function ProjectPreviewsRoute() {
  const projectId = useProjectId();
  const inspect = useCapability("preview.inspect");
  const history = usePreviewHistoryQuery(projectId, {
    enabled: inspect.allowed,
  });
  const paginationEndRef = useRef<HTMLParagraphElement>(null);
  const focusEndAfterFetch = useRef(false);

  useEffect(() => {
    if (focusEndAfterFetch.current && !history.isFetchingNextPage) {
      focusEndAfterFetch.current = false;
      if (!history.isFetchNextPageError && !history.hasNextPage) {
        paginationEndRef.current?.focus();
      }
    }
  }, [
    history.hasNextPage,
    history.isFetchingNextPage,
    history.isFetchNextPageError,
  ]);

  if (!inspect.allowed) {
    return (
      <DeniedState
        title="Preview History is not available to your account"
        description={`${inspect.reason ?? ""} Viewing historical Preview runs needs the "preview.inspect" permission, which Project Editors, Clinical Managers and Administrators hold.`}
      />
    );
  }

  if (history.isPending || !history.isFetchedAfterMount) {
    return (
      <QueryPendingState
        isPaused={history.isPaused}
        failureReason={history.failureReason}
        label="Loading Preview history"
        errorTitle="Preview history could not be loaded"
        rows={4}
        queryKey={queryKeys.previewHistory(projectId)}
      />
    );
  }

  if (
    !history.data ||
    (history.isError && isTerminalQueryError(history.error))
  ) {
    return (
      <QueryErrorState
        error={history.error}
        subject="Preview history"
        title="Preview history could not be loaded"
        deniedDescription="This Project's Preview history is not available to your account in this environment."
        treatNotFoundAsDenied
        onRetry={() => void history.refetch()}
      />
    );
  }

  const items = history.data.pages.flatMap((page) => page.items);
  const temporarilyUnavailable = items.some(
    (item) => item.inspectionState === "temporarily_unavailable",
  );

  function fetchOlder() {
    if (!history.hasNextPage || history.isFetchingNextPage) return;
    focusEndAfterFetch.current = true;
    void history.fetchNextPage();
  }

  return (
    <section aria-labelledby="preview-history-heading" className="space-y-4">
      <SimulatedPatientPanel projectId={projectId} />
      <div className="space-y-1">
        <h2 id="preview-history-heading" className="text-lg font-semibold">
          Preview history
        </h2>
        <p className="max-w-3xl text-sm text-muted-foreground">
          Synthetic Preview runs recorded for this Project, newest first. Run
          status comes from the target used at dispatch; the current Project
          target or candidate never rewrites an older row.
        </p>
      </div>

      <StaleDataNotice
        visible={history.isRefetchError && !history.isFetchNextPageError}
        updatedAt={history.dataUpdatedAt}
        onRetry={() => void history.refetch()}
      />

      {temporarilyUnavailable ? (
        <div
          role="status"
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm"
        >
          <span>
            Some recorded targets could not confirm run status. Their rows are
            shown without an inferred result.
          </span>
          <Button variant="outline" size="sm" onClick={() => void history.refetch()}>
            <RefreshCw aria-hidden="true" className="h-4 w-4" />
            Retry history
          </Button>
        </div>
      ) : null}

      {items.length === 0 ? (
        <EmptyState
          title="No Preview runs yet"
          description="Launch a synthetic patient Preview from this Project. Once the Editor records the dispatch, it will appear here without relying on a saved link."
        />
      ) : (
        <ol className="space-y-3">
          {items.map((item, index) => (
            <PreviewHistoryRow
              key={item.previewRunRef}
              item={item}
              position={index + 1}
              projectId={projectId}
            />
          ))}
        </ol>
      )}

      {history.isFetchNextPageError ? (
        <p role="alert" className="text-sm text-destructive">
          Older Preview runs could not be loaded. The rows above are unchanged.
        </p>
      ) : null}

      {history.hasNextPage ? (
        <Button
          variant="outline"
          disabled={history.isFetchingNextPage}
          onClick={fetchOlder}
        >
          {history.isFetchingNextPage
            ? "Loading older runs…"
            : history.isFetchNextPageError
              ? "Try loading older runs again"
              : "Show older runs"}
        </Button>
      ) : items.length > 0 ? (
        <p
          ref={paginationEndRef}
          tabIndex={-1}
          className="text-sm text-muted-foreground focus:outline-none"
        >
          End of Preview history.
        </p>
      ) : null}
    </section>
  );
}
